import { useState, type RefObject } from "react";
import { FinanceChoiceRow } from "@/components/teacher/FinanceChoiceRow";
import { TalkRequestDocuments } from "./TalkRequestDocuments";
import { teacherApi, teacherFinanceApi } from "@/services/api/teacher";
import type { PriorityRequestListItem } from "@/services/api/teacherPriority";
import type { IncomingRequest } from "@/services/api/teacher";
import { DECISION_TYPE_LABELS } from "@/types/director";
import { requestDocumentUi } from "@/utils/documentDue";
import { directorUiEnabled, showRequesterDeductionBand, showSubstitutePayBand } from "@/utils/financeUi";
import { formatMatchEta } from "@/utils/matchEta";
import { useTalkT } from "./useTalkT";

export function TalkOwnDesktop({
  requestId,
  request,
  fileInputRef,
}: {
  requestId: string;
  request: PriorityRequestListItem;
  fileInputRef: RefObject<HTMLInputElement>;
}) {
  const t = useTalkT();
  const directorOn = directorUiEnabled(request.director_enabled);
  const gateFrozen = directorOn && request.status === "director_pending";
  const docs = requestDocumentUi(directorOn, request);
  const payers: Record<string, string> = {
    school: t("talk_radar_payer_school"),
    private: t("talk_radar_payer_private"),
  };
  return (
    <>
      {directorOn && (request.director_decision_type || request.director_payment_type) && (
        <p>
          {request.director_decision_type && request.director_decision_type !== "info_requested" && (
            <span>
              {t("talk_radar_director", {
                decision: DECISION_TYPE_LABELS[request.director_decision_type] || request.director_decision_type,
              })}
            </span>
          )}
          {request.director_payment_type && (
            <span>{payers[request.director_payment_type] || request.director_payment_type}</span>
          )}
        </p>
      )}
      {request.combo_search_pending && !gateFrozen && (
        <p>{t("talk_radar_distribution", { eta: formatMatchEta(request.lesson_count) })}</p>
      )}
      <TalkOwnFinance requestId={requestId} request={request} />
      {docs.outstanding && <p>{t("talk_radar_docs_due")}</p>}
      {docs.show && (
        <TalkRequestDocuments
          requestId={requestId}
          fileInputRef={fileInputRef}
          canUpload={docs.canUpload}
          canDelete={docs.canDelete}
        />
      )}
    </>
  );
}

export function TalkOwnFinance({
  requestId,
  request,
}: {
  requestId: string;
  request: PriorityRequestListItem;
}) {
  const [override, setOverride] = useState<"ipb" | "salary" | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const personal = override ?? request.requester_deduction ?? null;
  if (!showRequesterDeductionBand(request, personal) || !request.requester_school_default) return null;
  const schoolDefault = request.requester_school_default;
  return (
    <FinanceChoiceRow
      groupName={`requester-deduction-${requestId}`}
      title="Abzug (privat):"
      options={[
        { value: "ipb", label: "IPB" },
        { value: "salary", label: "Lohnabzug" },
      ]}
      personalValue={personal}
      schoolDefault={schoolDefault}
      canEdit={request.can_change_requester_finance === true}
      lockReason={request.finance_lock_reason ?? null}
      bindingAt={request.finance_binding_at}
      pendingHint={
        !personal && request.show_requester_finance_hint
          ? "Abzug wählbar, sobald der Zahler privat ist."
          : null
      }
      saving={saving}
      error={error}
      onSwitch={(value) => {
        if (value !== "ipb" && value !== "salary") return;
        const previous = personal;
        setSaving(true);
        setError(null);
        setOverride(value);
        void teacherFinanceApi.setRequesterDeduction(requestId, value).catch(() => {
          setOverride(previous);
          setError("Speichern fehlgeschlagen");
        }).finally(() => setSaving(false));
      }}
    />
  );
}

export function TalkIncomingFinance({
  requestId,
  request,
  directorOn,
}: {
  requestId: string;
  request: IncomingRequest;
  directorOn: boolean;
}) {
  const [override, setOverride] = useState<"paid_out" | "lesson_account" | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const personal = override ?? request.finance_payout ?? null;
  if (!showSubstitutePayBand(directorOn, request) || !request.finance_school_default) return null;
  return (
    <FinanceChoiceRow
      groupName={`finance-payout-${requestId}`}
      title="Vergütung:"
      options={[
        { value: "lesson_account", label: "IPB" },
        { value: "paid_out", label: "Auszahlung" },
      ]}
      personalValue={personal}
      schoolDefault={request.finance_school_default}
      canEdit={request.finance_can_edit === true}
      lockReason={request.finance_lock_reason ?? null}
      bindingAt={request.finance_binding_at}
      saving={saving}
      error={error}
      onSwitch={(value) => {
        if (value !== "paid_out" && value !== "lesson_account") return;
        const previous = personal;
        setSaving(true);
        setError(null);
        setOverride(value);
        void teacherApi.setSubstitutePayout(requestId, value).catch(() => {
          setOverride(previous);
          setError("Speichern fehlgeschlagen");
        }).finally(() => setSaving(false));
      }}
    />
  );
}
